class TfSlideshow extends HTMLElement {
  constructor() {
    super();

    this.el = null;
    this.swiper = null;
    this.timer = null;
    this.mounted = false;
    this.retryCount = 0;
    this.maxRetry = 20;
    this.selectedBlockId = null;

    this.onResize = this.onResize.bind(this);
    this.onEditorEvent = this.onEditorEvent.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.updateLayoutVars = this.updateLayoutVars.bind(this);
    this.onAnnouncementClick = this.onAnnouncementClick.bind(this);
  }

  connectedCallback() {
    if (this.mounted) return;
    this.mounted = true;

    this.bindElements();
    this.updateLayoutVars();
    this.init();

    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', this.updateLayoutVars);
    document.addEventListener('shopify:section:load', this.onEditorEvent);
    document.addEventListener('shopify:section:reorder', this.onEditorEvent);
    document.addEventListener('shopify:section:select', this.onEditorEvent);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('click', this.onAnnouncementClick);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.updateLayoutVars);
    document.removeEventListener('shopify:section:load', this.onEditorEvent);
    document.removeEventListener('shopify:section:reorder', this.onEditorEvent);
    document.removeEventListener('shopify:section:select', this.onEditorEvent);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('click', this.onAnnouncementClick);

    this.clearTimer();
    this.destroy();
    this.mounted = false;
  }

  bindElements() {
    this.el = this.querySelector('.swiper.slideshow-swiper');
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  num(value, fallback) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  bool(value) {
    return value === 'true' || value === '1' || value === true;
  }

  getSlidesCount() {
    return this.el?.querySelectorAll('.swiper-wrapper > .swiper-slide').length || 0;
  }

  getSelectedBlockIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('block');
    } catch (e) {
      return null;
    }
  }
  
  slideToBlockId(blockId, speed = 300) {
    this.bindElements();
  
    const instance = this.el?.swiper || this.swiper;
    if (!instance || !blockId) return;
  
    const slides = Array.from(
      this.el.querySelectorAll('.swiper-wrapper > .swiper-slide[data-block-id]')
    ).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
  
    const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);
  
    if (index < 0) return;
  
    try { instance.autoplay?.stop?.(); } catch (e) {}
  
    if (
      instance.params.loop &&
      typeof instance.slideToLoop === 'function'
    ) {
      instance.slideToLoop(index, speed, false);
    } else {
      instance.slideTo(index, speed, false);
    }
  
    this.update();
  }

  getOptions() {

    const data = this.el.dataset || {};
    const paginationEl = this.querySelector('.slideshow-pagination');
    const nextEl = this.querySelector('.next-swiper');
    const prevEl = this.querySelector('.prev-swiper');

    const slidesCount = this.getSlidesCount();
    const autoPlay = this.bool(data.auto);
    const delay = this.num(data.delay, 3000);
    const loop = this.bool(data.loop) && slidesCount > 1;
    const effect = data.effect || 'slide';
    const layout = data.layout || 'default';
    const isDesignMode = window.Shopify && window.Shopify.designMode;

    const options = {
      direction: data.direction || 'horizontal',
      slidesPerView: 1,
      spaceBetween: this.num(data.space, 0),
      speed: this.num(data.speed, 800),
      effect,
      loop,
      grabCursor: true,
      allowTouchMove: true,
      watchOverflow: true,
      autoplay: autoPlay && slidesCount > 1 && !isDesignMode
        ? {
          delay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }
        : false,
    };

    if (effect === 'fade') {
      options.fadeEffect = {
        crossFade: true,
      };
    }

    if (layout === 'centered') {
      options.centeredSlides = true;
      options.slidesPerView = 'auto';
      options.effect = 'slide';
      options.spaceBetween = 20;
    }

    if (paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    if (nextEl && prevEl) {
      options.navigation = {
        nextEl,
        prevEl,
      };
    }

    return options;
  }

  init() {
    this.bindElements();

    if (!this.el || !window.Swiper) {
      if (this.retryCount < this.maxRetry) {
        this.retryCount += 1;
        this.timer = setTimeout(() => this.init(), 120);
      }
      return;
    }

    if ((this.el.offsetWidth || this.offsetWidth) === 0) {
      if (this.retryCount < this.maxRetry) {
        this.retryCount += 1;
        this.timer = setTimeout(() => this.init(), 120);
      }
      return;
    }

    this.retryCount = 0;
    this.destroy();

    this.swiper = new window.Swiper(this.el, this.getOptions());

    this.update();
    
    const selectedBlockId = this.selectedBlockId || this.getSelectedBlockIdFromUrl();
    
    requestAnimationFrame(() => {
      this.update();
    
      if (selectedBlockId) {
        this.selectedBlockId = selectedBlockId;
        this.slideToBlockId(selectedBlockId, 0);
      }
    });
    
    setTimeout(() => {
      this.update();
    
      if (selectedBlockId) {
        this.slideToBlockId(selectedBlockId, 0);
      }
    }, 120);
  }

  destroy() {
    const instance = this.el?.swiper || this.swiper;

    if (instance) {
      try {
        instance.destroy(true, true);
      } catch (e) { }
    }

    this.swiper = null;
  }

  update() {
    const instance = this.el?.swiper || this.swiper;
    if (!instance) return;

    try { instance.updateSize(); } catch (e) { }
    try { instance.updateSlides(); } catch (e) { }
    try { instance.updateProgress(); } catch (e) { }
    try { instance.updateSlidesClasses(); } catch (e) { }
    try { instance.pagination?.render?.(); } catch (e) { }
    try { instance.pagination?.update?.(); } catch (e) { }
    try { instance.navigation?.update?.(); } catch (e) { }
    try { instance.update(); } catch (e) { }
  }

  updateLayoutVars() {
    const header = document.querySelector('.section-header header.header, header.header, .header');
  
    const isTransparentHeader =
      header && header.classList.contains('header-transparent');
  
    const bars = document.querySelectorAll(
      '.announcementbar-section:not([hidden]), .section-topbar:not([hidden])'
    );
  
    const headerHeight = header && !isTransparentHeader
      ? header.offsetHeight
      : 0;
  
    let notificationHeight = 0;
  
    bars.forEach((bar) => {
      if (bar.offsetParent !== null) {
        notificationHeight += bar.offsetHeight || 0;
      }
    });
  
    document.documentElement.style.setProperty('--tf-header-h', `${headerHeight}px`);
    document.documentElement.style.setProperty('--tf-noti-h', `${notificationHeight}px`);
    document.documentElement.style.setProperty('--tf-vh', `${window.innerHeight}px`);
  }
  
  onAnnouncementClick(event) {
    if (event.target.closest('[data-announcement-close]')) {
      setTimeout(this.updateLayoutVars, 450);
    }
  }

  onResize() {
    this.update();
  }

  onEditorEvent(event) {
    if (!event?.target) return;
    if (!event.target.contains(this) && !this.contains(event.target)) return;

    this.clearTimer();
    this.timer = setTimeout(() => this.init(), 80);
  }

  onBlockSelect(event) {
    if (!event?.target) return;
    if (!event.target.contains(this) && !this.contains(event.target)) return;
  
    const blockId =
      event.detail?.blockId ||
      event.target.closest('[data-block-id]')?.dataset.blockId ||
      this.getSelectedBlockIdFromUrl();
  
    if (blockId) {
      this.selectedBlockId = blockId;
    }
  
    setTimeout(() => {
      if (this.selectedBlockId) {
        this.slideToBlockId(this.selectedBlockId);
      } else {
        this.slideToSelectedBlock(event.target);
      }
    }, 120);
  }

  slideToSelectedBlock(target) {
    this.bindElements();

    const instance = this.el?.swiper || this.swiper;
    if (!instance) return;

    const slide = target.closest('.swiper-slide');
    if (!slide || !this.el.contains(slide)) return;

    const realIndex = slide.getAttribute('data-swiper-slide-index');

    if (
      instance.params.loop &&
      realIndex !== null &&
      typeof instance.slideToLoop === 'function'
    ) {
      instance.slideToLoop(parseInt(realIndex, 10), 300, false);
      this.update();
      return;
    }

    const slides = Array.from(instance.slides || []);
    const index = slides.indexOf(slide);

    if (index >= 0) {
      instance.slideTo(index, 300, false);
      this.update();
    }
  }
}

if (!customElements.get('tf-slideshow')) {
  customElements.define('tf-slideshow', TfSlideshow);
}
